import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../auth/AuthProvider';
import { makeThemedStyles } from '../v10/theme';
import { Atmos, Chip, SectionTitle } from '../v10/ui';
import { ScreenHelp } from '../v10/ScreenHelp';
import { astrsHelp } from '../olmps/help/astrsHelp';
import type { ToolProps } from '../olmps/ToolHost';
import ProjectsSection from './astrs/ProjectsSection';
import { ChartsSection } from './astrs/ChartsSection';

// ---------------------------------------------------------------------------
// Native replica of the web ASTRS hub (src/app/astrs/page.tsx), which is a
// thin router to two sections: Projects (/projects) and Charts (/charts).
// Mobile has no nested-route stack for this tool, so the hub itself renders
// as a two-tab layout ("Projects" | "Charts") that swaps between
// ProjectsSection and ChartsSection in place, matching the web hub's two
// destinations without introducing navigation.
// ---------------------------------------------------------------------------

type AstrsTab = 'projects' | 'charts';

export function AstrsTool(_props: ToolProps) {
  const { session } = useAuth();
  const userId = session?.user.id;

  const [tab, setTab] = useState<AstrsTab>('projects');

  const styles = useStyles();

  return (
    <Atmos style={styles.root}>
      <View style={styles.header}>
        <SectionTitle>ASTRS</SectionTitle>
        <Text style={styles.subtitle}>Projects &amp; Charts</Text>
        <ScreenHelp help={astrsHelp} style={styles.helpBtn} />
      </View>

      <View style={styles.tabRow}>
        <Chip label="Projects" active={tab === 'projects'} onPress={() => setTab('projects')} style={styles.tabButton} />
        <Chip label="Charts" active={tab === 'charts'} onPress={() => setTab('charts')} style={styles.tabButton} />
      </View>

      <View style={styles.body}>
        {tab === 'projects' ? <ProjectsSection userId={userId} /> : <ChartsSection userId={userId} />}
      </View>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    root: { flex: 1 },
    header: { paddingHorizontal: 16, paddingTop: 12 },
    helpBtn: { position: 'absolute', right: 16, top: 14 },
    subtitle: { fontSize: 12, color: t.ivorySoft, marginTop: 2 },
    tabRow: {
      flexDirection: 'row',
      gap: 10,
      paddingHorizontal: 16,
      paddingTop: 12,
      paddingBottom: 8,
    },
    tabButton: { flex: 1, alignItems: 'center', paddingVertical: 9 },
    body: { flex: 1 },
  }),
);
